import { getApiBaseUrl, type ApiBaseUrlEnv } from './getApiBaseUrl'

function resolveApiBaseUrl(): string {
  return getApiBaseUrl(
    import.meta.env as unknown as ApiBaseUrlEnv,
    typeof window !== 'undefined' ? window : undefined,
  )
}

export interface ApiError {
  message: string
  statusCode: number
}

class ApiClient {
  private token: string | null = null

  setToken(token: string) {
    this.token = token
  }

  clearToken() {
    this.token = null
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const base = resolveApiBaseUrl()
    const pathPart = path.startsWith('/') ? path : `/${path}`
    const url = `${base}${pathPart}`

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      const text = await response.text()
      let body: { message?: unknown } = {}
      try {
        body = text ? JSON.parse(text) : {}
      } catch {
        body = { message: text.slice(0, 200) || response.statusText }
      }
      const rawMsg = body.message
      const message =
        typeof rawMsg === 'string'
          ? rawMsg
          : Array.isArray(rawMsg)
            ? rawMsg.map(String).join(', ')
            : text?.slice(0, 200) || response.statusText || 'Request failed'
      const err = new Error(`[${response.status}] ${message}`)
      ;(err as any).statusCode = response.status
      throw err
    }

    return response.json()
  }

  get<T>(path: string): Promise<T> {
    return this.request('GET', path)
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request('POST', path, body)
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request('PUT', path, body)
  }

  delete<T>(path: string): Promise<T> {
    return this.request('DELETE', path)
  }

  /** POST multipart (e.g. photo upload). Do not set Content-Type — boundary is set by the browser. */
  async postForm<T>(path: string, form: FormData): Promise<T> {
    const base = resolveApiBaseUrl()
    const pathPart = path.startsWith('/') ? path : `/${path}`
    const url = `${base}${pathPart}`

    const headers: HeadersInit = {}
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: form,
    })

    if (!response.ok) {
      const text = await response.text()
      let parsed: { message?: unknown } = {}
      try {
        parsed = text ? JSON.parse(text) : {}
      } catch {
        parsed = { message: text.slice(0, 200) || response.statusText }
      }
      const rawMsg = parsed.message
      const message =
        typeof rawMsg === 'string'
          ? rawMsg
          : Array.isArray(rawMsg)
            ? rawMsg.map(String).join(', ')
            : text?.slice(0, 200) || response.statusText || 'Request failed'
      const err = new Error(`[${response.status}] ${message}`)
      ;(err as any).statusCode = response.status
      throw err
    }

    return response.json()
  }
}

export const apiClient = new ApiClient()
